/**
 * useServiceOrderDetails - Dados e ações do modal de detalhes da OS (Fase 6)
 *
 * Detalhes, linha do tempo (andamentos + histórico), andamentos e troca de
 * situação, 100% via RPC. O cliente `supabase` é preservado com `.call`.
 *
 * RELEASE: 2026-10-09 v1.2.0 - Edição segura de texto e anexos dos andamentos.
 * RELEASE: 2026-10-09 v1.1.0 - Andamentos ricos, anexos e controle do cronômetro.
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial.
 */
import type { ServiceOrderStockSelection } from '@/hooks/useServiceOrderStock';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { STOCK_REFRESH_QUERY_KEYS } from '@/hooks/useStockMovements';
import type { ServiceOrderRow, ServiceOrderStatus } from '@/hooks/useServiceOrders';

const rpc = (name: string, args: Record<string, unknown>) => (supabase.rpc as any).call(supabase, name, args);

/** Limite de caracteres de um andamento (mesmo valor validado no servidor). */
export const SERVICE_ORDER_COMMENT_MAX = 5000;

/** Projeção segura da Pessoa exibida na ficha somente leitura do prestador. */
export interface ServiceOrderProviderDetails {
  id: string;
  person_type: string | null;
  document: string | null;
  name: string;
  legal_name: string | null;
  trade_name: string | null;
  email: string | null;
  phone: string | null;
  cep: string | null;
  street: string | null;
  address_number: string | null;
  complement: string | null;
  district: string | null;
  city: string | null;
  state: string | null;
  notes: string | null;
  is_active: boolean;
  roles: string[];
  service_area: string | null;
}

export interface ServiceOrderDetails extends ServiceOrderRow {
  description: string | null;
  category_name: string;
  responsible_user_id: string | null;
  responsible_name: string | null;
  created_by_name: string | null;
  service_type_duration: number;
  requires_vehicle: boolean;
  stock_movement: string;
  address_cep: string | null; address_street: string | null; address_number: string | null;
  address_complement: string | null; address_district: string | null;
  address_reference: string | null; address_phone: string | null; address_contact_name: string | null; address_maps_url: string | null;
  customer_phone: string | null;
  /** BR-OS-026: contato principal do cliente (aba Chat) */
  customer_contact_id?: string | null;
  customer_contact_name?: string | null;
  category_color: string | null;
  phase_sort_order: number;
  total_category_phases: number;
  vehicle_brand_name: string | null;
  vehicle_model_name: string | null;
  vehicle_year: number | null;
  vehicle_color: string | null;
  provider_details: ServiceOrderProviderDetails | null;
  responsible_avatar_url: string | null;
  /** BR-OS-020: vendedor da OS */
  seller_id?: string | null;
  seller_name?: string | null;
  seller_avatar_url?: string | null;
  created_by_avatar_url: string | null;
  started_at: string | null;
  reviewed_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  comments_count: number;
}

export interface ServiceOrderTimelineItem {
  kind: 'comment' | 'event';
  id: string;
  content?: string;
  content_html?: string | null;
  mentioned_user_ids?: string[];
  attachments?: ServiceOrderAttachment[];
  // BR-OS-033: inclui schedule_cancelled e schedule_rescheduled (gatilho v1.4.0)
  event_type?: 'created' | 'status' | 'phase' | 'provider' | 'responsible' | 'schedule' | 'schedule_cancelled' | 'schedule_rescheduled' | 'timer_paused' | 'timer_resumed' | 'install_override_approved' | 'checklist_stage_released' | 'checklist_status'
    // 2026-10-17: serviços do negócio na O.S.
    | 'deal_linked' | 'services_synced' | 'services_answered' | 'service_added_field' | 'service_field_reviewed'
    | 'service_added_manual' | 'service_quantity_changed' | 'service_removed_manual' | 'service_notes_changed'
    | 'created_by_workflow';
  old_value?: string | null;
  new_value?: string | null;
  note?: string | null;
  /** Dados extras do evento (remessa: rótulo do item/vínculo e campos alterados) */
  metadata?: Record<string, unknown> | null;
  author_name: string | null;
  avatar_url: string | null;
  /** Origem do autor: 'user' (membro do tenant) ou 'provider' (prestador do portal) */
  author_kind?: 'user' | 'provider';
  is_mine: boolean;
  created_at: string;
  updated_at?: string;
}

/** Metadados seguros de um arquivo privado vinculado ao andamento. */
export interface ServiceOrderAttachment {
  id: string;
  file_name: string;
  file_type: string;
  mime_type: string | null;
  file_size: number;
  storage_path: string;
  created_at: string;
}

export interface AddServiceOrderCommentInput {
  content: string;
  contentHtml?: string;
  mentionedUserIds?: string[];
}

export interface UpdateServiceOrderCommentInput extends AddServiceOrderCommentInput {
  commentId: string;
  removedAttachmentIds?: string[];
}

/**
 * Transições de situação (BR-OS-003). Espelha a matriz do servidor apenas
 * para exibir os botões; quem decide é `change_service_order_status`.
 * `param` = parâmetro do perfil exigido; `needsNote` = observação obrigatória.
 */
export interface ServiceOrderTransition {
  to: ServiceOrderStatus;
  label: string;
  param?: 'os_param_start' | 'os_param_review' | 'os_param_reopen';
  needsNote?: boolean;
  /** v1.4.0: Concluir do prestador abre a seleção de estoque (quando o tipo movimenta). */
  needsStock?: boolean;
  variant?: 'default' | 'outline' | 'destructive';
}

export function getServiceOrderTransitions(status: ServiceOrderStatus): ServiceOrderTransition[] {
  switch (status) {
    case 'open':
    // Logística: Entregue e Reagendar seguem como Aberta
    case 'shipment_delivered':
    case 'to_reschedule':
      return [
        { to: 'in_progress', label: 'Iniciar', param: 'os_param_start' },
        { to: 'cancelled', label: 'Cancelar', needsNote: true, variant: 'destructive' },
      ];
    case 'scheduled':
      return [
        { to: 'in_progress', label: 'Iniciar', param: 'os_param_start' },
        // BR-OS-030: "Desagendar" saiu do topo — gestão de agendamento fica na aba Agenda
        { to: 'cancelled', label: 'Cancelar', needsNote: true, variant: 'destructive' },
      ];
    case 'in_progress':
      return [
        { to: 'awaiting_review', label: 'Concluir', needsStock: true },
        { to: 'cancelled', label: 'Cancelar', needsNote: true, variant: 'destructive' },
      ];
    case 'awaiting_review':
      return [
        { to: 'completed', label: 'Revisado', param: 'os_param_review' },
        // BR-OS-034: devolver leva para "Em correção" (não volta a "Em andamento")
        { to: 'in_correction', label: 'Devolver para correção', param: 'os_param_review', needsNote: true, variant: 'outline' },
      ];
    case 'in_correction':
      // BR-OS-037: sem Cancelar — o estoque já foi movimentado por esta OS
      return [{ to: 'awaiting_review', label: 'Concluir', needsStock: true }];
    // Logística: situação controlada pela remessa — só Cancelar é manual
    case 'awaiting_shipment':
    case 'shipment_in_transit':
      return [{ to: 'cancelled', label: 'Cancelar', needsNote: true, variant: 'destructive' }];
    case 'completed':
      return [{ to: 'in_correction', label: 'Reabrir', param: 'os_param_reopen', variant: 'outline' }];
    default:
      return [];
  }
}

export function useServiceOrderDetails(id: string | null) {
  const { currentTenant } = useAuth();
  const qc = useQueryClient();
  const hint = currentTenant?.id ?? null;
  const enabled = !!id && !!hint;

  const details = useQuery({
    queryKey: ['service-orders', 'details', hint, id],
    enabled,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_order_details', { p_id: id, p_tenant_hint: hint });
      if (error) throw error;
      return data as ServiceOrderDetails;
    },
  });

  const timeline = useQuery({
    queryKey: ['service-orders', 'timeline', hint, id],
    enabled,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_order_timeline', { p_id: id, p_tenant_hint: hint });
      if (error) throw error;
      return (data ?? []) as ServiceOrderTimelineItem[];
    },
  });

  // Toda alteração atualiza modal, Kanban e listagem (mesma raiz de cache)
  // Fase 8: também atualiza os checklists (Iniciar gera a primeira etapa)
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['service-orders'] });
    qc.invalidateQueries({ queryKey: ['service-order-checklists', id] });
  };

  const addComment = useMutation({
    mutationFn: async ({ content, contentHtml, mentionedUserIds }: AddServiceOrderCommentInput) => {
      const { data, error } = await rpc('add_service_order_comment', {
        p_id: id,
        p_content: content.slice(0, SERVICE_ORDER_COMMENT_MAX),
        p_content_html: contentHtml?.slice(0, 20000) || null,
        p_mentioned_user_ids: mentionedUserIds?.slice(0, 20) ?? [],
        p_tenant_hint: hint,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: refresh,
  });

  const deleteComment = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await rpc('delete_service_order_comment', { p_comment_id: commentId, p_tenant_hint: hint });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const updateComment = useMutation({
    mutationFn: async ({ commentId, content, contentHtml, mentionedUserIds, removedAttachmentIds }: UpdateServiceOrderCommentInput) => {
      const { data, error } = await rpc('update_service_order_comment', {
        p_comment_id: commentId,
        p_content: content.slice(0, SERVICE_ORDER_COMMENT_MAX),
        p_content_html: contentHtml?.slice(0, 20000) || null,
        p_mentioned_user_ids: mentionedUserIds?.slice(0, 20) ?? [],
        p_removed_attachment_ids: removedAttachmentIds?.slice(0, 20) ?? [],
        p_tenant_hint: hint,
      });
      if (error) throw error;
      return data as { removed_paths?: string[] };
    },
    onSuccess: refresh,
  });

  // Fase 9: `stock` leva os itens instalados/retirados na conclusão (servidor revalida)
  const changeStatus = useMutation({
    mutationFn: async ({ status, note, stock, phaseId }: { status: ServiceOrderStatus; note?: string; stock?: ServiceOrderStockSelection | null; phaseId?: string | null }) => {
      const { error } = await rpc('change_service_order_status', {
        p_id: id, p_status: status, p_note: note?.slice(0, 1000) || null, p_stock: stock ?? null, p_phase_id: phaseId ?? null, p_tenant_hint: hint,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      refresh();
      qc.invalidateQueries({ queryKey: ['service-order-stock', id] });
      // A conclusão pode movimentar estoque: atualiza Equipamentos do veículo e listas do Estoque
      STOCK_REFRESH_QUERY_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      qc.invalidateQueries({ queryKey: ['vehicle-timeline'] });
    },
  });

  const pauseTimer = useMutation({
    mutationFn: async (reason?: string) => {
      const { error } = await rpc('pause_service_order', { p_id: id, p_reason: reason?.slice(0, 500) || null, p_tenant_hint: hint });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const resumeTimer = useMutation({
    mutationFn: async () => {
      const { error } = await rpc('resume_service_order', { p_id: id, p_tenant_hint: hint });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { details, timeline, addComment, updateComment, deleteComment, changeStatus, pauseTimer, resumeTimer };
}
